import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/layout';
import { Button, Icon, IconButton, Progress, Skeleton } from '@chakra-ui/react';
import { MdCheckCircle, MdClose, MdMood, MdOutlineWarning, MdRadioButtonUnchecked, MdTaskAlt } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '@chakra-ui/modal';
import { AuthContext } from '../../auth/lib/authContext';
import useTranslation from '../../lib/i18n/useTranslation';
import useOnboardingApi from '../lib/useOnboardingApi';

export default function OnboardingChecklist(props) {
  const { variant = 'full' } = props;
  const { t } = useTranslation();
  const { profile } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const onboardingApi = useOnboardingApi();
  const [isFetching, setIsFetching] = useState(true);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isSkipped, setIsSkipped] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [completedAt, setCompletedAt] = useState(null);
  const [completedSeenStorageKey, setCompletedSeenStorageKey] = useState(null);
  const [firstPersonaId, setFirstPersonaId] = useState(null);
  const [experienceWithProductId, setExperienceWithProductId] = useState(null);
  const [completion, setCompletion] = useState({
    productCreated: false,
    personaCreated: false,
    experienceWithProductCreated: false,
    twoDifferentInsightsCreated: false,
    insightArchived: false,
  });

  const skipOnboarding = async () => {
    if (!profile?.id) {
      return;
    }
    await onboardingApi.upsertOnboardingState(profile.id, {
      dismissed: true,
      current_step: 'skipped',
      completed_at: null,
    });
    setShowSkipModal(false);
    setIsHidden(true);
    setIsSkipped(true);
  };

  const getProgress = async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!profile?.id) {
        return;
      }
      if (!silent && !hasFetchedOnce) {
        setIsFetching(true);
      }
      const progress = await onboardingApi.fetchOnboardingProgress(profile.id);
      const stateInDb = progress
        ? {
          dismissed: progress.dismissed,
          current_step: progress.current_step,
          completed_at: progress.completed_at,
        }
        : null;
      setCompletedAt(stateInDb?.completed_at || null);
      setIsHidden(!!stateInDb?.dismissed);
      setIsSkipped(stateInDb?.current_step === 'skipped');
      const hasProduct = !!progress?.has_product;
      const hasPersona = !!progress?.has_persona;
      const foundExperienceId = progress?.experience_with_product_id || null;
      setFirstPersonaId(progress?.first_persona_id || null);
      setExperienceWithProductId(foundExperienceId);
      const hasExperienceWithProduct = !!progress?.has_experience_with_product;
      const hasTwoDifferentInsightsInExperience = !!progress?.has_two_different_insights;
      const hasArchivedInsightInExperience = !!progress?.has_archived_insight;

      setCompletion({
        productCreated: hasProduct,
        personaCreated: hasPersona,
        experienceWithProductCreated: hasExperienceWithProduct,
        twoDifferentInsightsCreated: hasTwoDifferentInsightsInExperience,
        insightArchived: hasArchivedInsightInExperience,
      });

      const currentStep = !hasProduct
        ? 'create_product'
        : !hasPersona
          ? 'create_persona'
          : !hasExperienceWithProduct
            ? 'create_experience'
            : !hasTwoDifferentInsightsInExperience
              ? 'create_two_insights'
              : !hasArchivedInsightInExperience
                ? 'archive_insight'
              : 'completed';
      const hasCompleted = hasProduct
        && hasPersona
        && hasExperienceWithProduct
        && hasTwoDifferentInsightsInExperience
        && hasArchivedInsightInExperience;
      const hasCompletedPersisted = !!stateInDb?.completed_at;
      const shouldSetCompletedAt = hasCompleted && !stateInDb?.completed_at;
      const shouldClearCompletedAt = !hasCompleted && !!stateInDb?.completed_at;
      const shouldUpdateCurrentStep = stateInDb?.current_step !== currentStep;

      const hasSkipped = stateInDb?.current_step === 'skipped';
      let updatedOnboardingState = stateInDb;
      if (!stateInDb || shouldSetCompletedAt || shouldClearCompletedAt || (shouldUpdateCurrentStep && !hasSkipped)) {
        const updatedState = await onboardingApi.upsertOnboardingState(profile.id, {
          dismissed: !!stateInDb?.dismissed,
          current_step: hasSkipped ? 'skipped' : (hasCompleted ? 'completed' : currentStep),
          completed_at: hasSkipped || !hasCompleted ? null : (stateInDb?.completed_at || new Date().toISOString()),
        });
        if (updatedState) {
          updatedOnboardingState = updatedState;
          setIsHidden(!!updatedState.dismissed);
          setCompletedAt(updatedState.completed_at || null);
        }
      }
      if (hasCompleted && updatedOnboardingState?.completed_at) {
        const completedKey = `persona_onboarding_completed_seen_${profile.id}_${updatedOnboardingState.completed_at}`;
        setCompletedSeenStorageKey(completedKey);
        const seenCompletedAt = localStorage.getItem(completedKey);
        if (seenCompletedAt !== 'true') {
          setShowCompletedModal(true);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
      setHasFetchedOnce(true);
    }
  };

  useEffect(() => {
    getProgress();
  }, [profile?.id, location.pathname]);

  const steps = useMemo(() => {
    return [
      {
        id: 'product',
        title: t('home.onboardingStepProductTitle'),
        description: t('home.onboardingStepProductDesc'),
        done: completion.productCreated,
        actionPath: '/products',
      },
      {
        id: 'persona',
        title: t('home.onboardingStepPersonaTitle'),
        description: t('home.onboardingStepPersonaDesc'),
        done: completion.personaCreated,
        actionPath: '/persona',
      },
      {
        id: 'experience',
        title: t('home.onboardingStepExperienceTitle'),
        description: t('home.onboardingStepExperienceDesc'),
        done: completion.experienceWithProductCreated,
        actionPath: firstPersonaId ? `/persona/${firstPersonaId}` : '/persona',
      },
      {
        id: 'insight',
        title: t('home.onboardingStepTwoInsightsTitle'),
        description: t('home.onboardingStepTwoInsightsDesc'),
        done: completion.twoDifferentInsightsCreated,
        actionPath: experienceWithProductId
          ? `/experiences/${experienceWithProductId}`
          : (firstPersonaId ? `/persona/${firstPersonaId}` : '/persona'),
      },
      {
        id: 'archive',
        title: t('home.onboardingStepArchiveInsightTitle'),
        description: t('home.onboardingStepArchiveInsightDesc'),
        done: completion.insightArchived,
        actionPath: experienceWithProductId
          ? `/experiences/${experienceWithProductId}`
          : (firstPersonaId ? `/persona/${firstPersonaId}` : '/persona'),
      },
    ];
  }, [completion, firstPersonaId, experienceWithProductId, t]);

  const completedCount = steps.filter((step) => step.done).length;
  const totalSteps = steps.length;
  const progressValue = Math.round((completedCount / totalSteps) * 100);
  const nextStep = steps.find((step) => !step.done);
  const currentStepIndex = nextStep ? steps.findIndex((step) => step.id === nextStep.id) : totalSteps - 1;
  const isCompleted = !!completedAt || completedCount === totalSteps;
  const isOnCurrentStepPage = !!(nextStep && location.pathname === nextStep.actionPath);

  useEffect(() => {
    if (!profile?.id || !isOnCurrentStepPage || isCompleted) {
      return;
    }
    const refreshInterval = setInterval(() => {
      getProgress({ silent: true });
    }, 2000);
    return () => {
      clearInterval(refreshInterval);
    };
  }, [profile?.id, isOnCurrentStepPage, isCompleted, location.pathname]);

  if (isFetching) {
    if (variant === 'compact') {
      return null;
    }
    return (
      <Flex px={3} mb={3}>
        <Flex
          w="100%"
          border="1px solid"
          borderColor="divider.grey"
          backgroundColor="white"
          p={3}
          flexDirection="column"
          gap={2}
        >
          <Skeleton height="20px" />
          <Skeleton height="10px" />
          <Skeleton height="10px" />
        </Flex>
      </Flex>
    );
  }

  if (isCompleted) {
    return (
      <>
        <Modal
          isOpen={showCompletedModal}
          onClose={() => {
            if (completedSeenStorageKey) {
              localStorage.setItem(completedSeenStorageKey, 'true');
            }
            setShowCompletedModal(false);
          }}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('home.onboardingCompletedTitle')}</ModalHeader>
            <ModalBody>
              <Flex p={3} flexDirection={"column"} alignItems={"center"} justifyContent={"center"} gap={2}>
                <Icon as={MdMood} color="fill.green" boxSize="40px" />
                <Text>{t('home.onboardingCompletedMessage')}</Text>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="orangeOutline"
                onClick={() => {
                  if (completedSeenStorageKey) {
                    localStorage.setItem(completedSeenStorageKey, 'true');
                  }
                  setShowCompletedModal(false);
                }}
              >
                {t('common.close')}
              </Button>
              <Button
                variant="orangeFill"
                ml={2}
                onClick={() => {
                  if (completedSeenStorageKey) {
                    localStorage.setItem(completedSeenStorageKey, 'true');
                  }
                  setShowCompletedModal(false);
                  navigate('/home');
                }}
              >
                {t('home.onboardingGoToHome')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  if (isHidden) {
    if (variant === 'compact' || isSkipped) {
      return null;
    }
    return (
      <Flex px={3} mb={3}>
        <Button
          variant="greyOutline"
          leftIcon={<Icon as={MdTaskAlt} boxSize="16px" />}
          onClick={async () => {
            if (!profile?.id) {
              return;
            }
            const updatedState = await onboardingApi.showOnboarding(profile.id);
            setIsHidden(updatedState ? !!updatedState.dismissed : false);
          }}
        >
          {t('home.resumeOnboarding')}
        </Button>
      </Flex>
    );
  }

  if (variant === 'compact') {
    if (location.pathname === '/home' || !nextStep) {
      return null;
    }
    return (
      <>
        <Flex px={3} py={2} borderBottom="1px solid" borderColor="divider.grey" bg="white" justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap={2} minW={0}>
            <Icon as={MdTaskAlt} color="text.medium.grey" boxSize="16px" />
            <Text fontSize="14px" color="text.medium.grey">
              {t('home.onboardingCurrentStepPrefix')} {currentStepIndex + 1}/{totalSteps}
            </Text>
            <Text fontSize="14px" noOfLines={1}>
              {nextStep.title}
            </Text>
          </Flex>
          <Flex alignItems="center" gap={2}>
            <Button
              size="sm"
              variant="orangeTransparent"
              onClick={() => {
                setShowSkipModal(true);
              }}
            >
              {t('home.onboardingSkip')}
            </Button>
          </Flex>
        </Flex>
        <Modal isOpen={showSkipModal} onClose={() => setShowSkipModal(false)}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('home.onboardingSkipConfirmTitle')}</ModalHeader>
            <ModalBody>
              <Box p={3}>
                <Flex
                  border="1px"
                  borderColor="divider.grey"
                  borderRadius="2px"
                  backgroundColor="white"
                  alignItems={'center'}
                  p={3}
                >
                  <Icon
                    as={MdOutlineWarning}
                    color={'text.medium.orange'}
                    boxSize={'18px'}
                    mr={3}
                  />
                  <Text>{t('home.onboardingSkipConfirmMessage')}</Text>
                </Flex>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button
                variant='orangeOutline'
                onClick={() => setShowSkipModal(false)}
              >{t('common.close')}</Button>
              <Button
                variant="orangeFill"
                ml={2}
                onClick={skipOnboarding}
              >{t('home.onboardingSkip')}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Flex px={3} mb={3}>
      <Flex
        w="100%"
        border="1px solid"
        borderColor="divider.grey"
        backgroundColor="white"
        flexDirection="column"
      >
        <Flex
          px={3}
          py={2}
          borderBottom="1px solid"
          borderColor="divider.grey"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center" gap={2}>
            <Icon as={MdTaskAlt} color="text.medium.grey" boxSize="18px" />
            <Text fontSize="16px">{t('home.onboardingTitle')}</Text>
          </Flex>
          <IconButton
            variant="greyTransparent"
            icon={<Icon as={MdClose} boxSize="18px" />}
            aria-label={t('common.close')}
            onClick={skipOnboarding}
          />
        </Flex>
        <Flex p={3} flexDirection="column" gap={3}>
          <Box>
            <Flex mb={2} justifyContent="space-between" alignItems="center">
              <Text color="text.medium.grey">{t('home.onboardingDesc')}</Text>
              <Text color="text.medium.grey">{completedCount}/{totalSteps}</Text>
            </Flex>
            <Progress value={progressValue} colorScheme="green" borderRadius="2px" bg="background.component.grey" />
          </Box>
          <Flex flexDirection="column" border="1px solid" borderColor="divider.grey">
            {steps.map((step, index) => {
              const isActive = nextStep && nextStep.id === step.id;
              return (
                <Flex
                  key={step.id}
                  px={3}
                  py={2}
                  borderBottom={index < steps.length - 1 ? '1px solid' : 'none'}
                  borderColor="divider.grey"
                  alignItems="center"
                  bg={isActive ? 'background.orange' : 'white'}
                >
                  <Flex alignItems="center" gap={2} minW={0}>
                    <Icon
                      as={step.done ? MdCheckCircle : MdRadioButtonUnchecked}
                      color={step.done ? 'fill.green' : 'text.medium.grey'}
                      boxSize="18px"
                    />
                    <Text color={step.done ? 'text.medium.grey' : 'text.dark'} noOfLines={1}>
                      {step.title}
                    </Text>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
